import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, AlertTriangle, Loader2, ArrowUpRight, ArrowDownRight, Plus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useRegion } from "@/context/RegionContext";
import { toast } from "sonner";

const MIN_TOPUP = 20;
const LOW_BALANCE = 10;

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-MY", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

const TX_LABEL = {
  topup: "Top-up",
  daily_deduction: "Daily bid deduction",
  refund: "Refund",
};

export default function MyWallet() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { setRegion } = useRegion();
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState(MIN_TOPUP);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/wallet", { params: { region: "MY" } });
      setWallet(data.wallet);
      setTxns(data.transactions || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Wallet 🇲🇾 — PodiumOne";
    setRegion("MY");
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    load();
  }, [user, authLoading, load, navigate, setRegion]);

  const topup = async () => {
    const amt = Number(amount);
    if (!amt || amt < MIN_TOPUP) {
      toast.error(`Minimum top-up is RM ${MIN_TOPUP.toFixed(2)}`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/wallet/topup", {
        amount: amt,
        origin_url: window.location.origin,
        region: "MY",
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url; // redirect to Stripe Checkout (FPX/GrabPay)
      } else {
        toast.error("Could not start checkout. Please try again.");
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div>
        <Navbar />
        <div className="flex justify-center pt-40"><Loader2 className="w-8 h-8 animate-spin text-white/40" /></div>
      </div>
    );
  }

  const balance = Number(wallet?.balance || 0);
  const low = balance < LOW_BALANCE;

  return (
    <div>
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 pt-28 pb-24">
        <div className="flex items-center gap-2 mb-2">
          <Wallet className="w-5 h-5 text-emerald-400" />
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/40">Prepaid wallet 🇲🇾</span>
        </div>
        <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tighter mb-6">Your MYR wallet</h1>

        {/* Balance card */}
        <div className="glass-strong rounded-3xl p-6 sm:p-8 mb-5" data-testid="wallet-balance-card">
          <div className="text-sm text-white/50 mb-1">Current balance</div>
          <div data-testid="wallet-balance" className="font-display font-black text-5xl sm:text-6xl tracking-tighter text-emerald-400 tabular-nums">
            RM {balance.toFixed(2)}
          </div>
        </div>

        {low && (
          <div data-testid="wallet-low-alert" className="flex items-start gap-3 rounded-2xl border border-yellow-500/50 bg-yellow-500/10 p-4 mb-5">
            <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-100 font-medium">⚠️ Low balance: Top up to keep your bids active.</div>
          </div>
        )}

        {/* Top-up */}
        <div className="glass rounded-2xl p-6 mb-8" data-testid="wallet-topup">
          <h2 className="font-display font-bold text-lg tracking-tight mb-1 flex items-center gap-2"><Plus className="w-4 h-4 text-emerald-400" /> Top up</h2>
          <p className="text-sm text-white/50 mb-4">Minimum RM {MIN_TOPUP.toFixed(2)}. Pay securely via FPX or GrabPay.</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <Label className="text-white/70 text-sm">Amount (RM)</Label>
              <Input
                data-testid="wallet-topup-input"
                type="number"
                min={MIN_TOPUP}
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1.5 bg-white/5 border-white/15 focus-visible:ring-emerald-500/50 text-lg"
              />
            </div>
            <Button
              data-testid="wallet-topup-btn"
              onClick={topup}
              disabled={submitting}
              className="rounded-full h-12 px-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-semibold active:scale-95 transition-all shrink-0"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Top up RM {Number(amount || 0).toFixed(0)}</>}
            </Button>
          </div>
          <div className="flex items-center gap-2 mt-3 text-xs text-white/40">
            <span className="rounded-md bg-white/10 px-2 py-0.5">FPX</span>
            <span className="rounded-md bg-white/10 px-2 py-0.5">GrabPay</span>
            <span className="rounded-md bg-white/10 px-2 py-0.5">Card</span>
          </div>
        </div>

        {/* Transactions */}
        <div>
          <h2 className="font-display font-bold text-lg tracking-tight mb-4">Transaction history</h2>
          {txns.length === 0 ? (
            <div className="glass rounded-2xl p-8 text-center text-white/40" data-testid="wallet-txns-empty">
              No transactions yet. Top up to get started.
            </div>
          ) : (
            <div className="glass rounded-2xl overflow-hidden" data-testid="wallet-txns-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-white/40 text-xs uppercase tracking-wider border-b border-white/10">
                    <th className="px-4 py-3 font-semibold">Type</th>
                    <th className="px-4 py-3 font-semibold">Date</th>
                    <th className="px-4 py-3 font-semibold text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.map((t) => {
                    const credit = t.amount >= 0;
                    return (
                      <tr key={t.tx_id} data-testid={`wallet-txn-${t.tx_id}`} className="border-b border-white/5 last:border-0">
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-2">
                            {credit ? <ArrowUpRight className="w-4 h-4 text-emerald-400" /> : <ArrowDownRight className="w-4 h-4 text-white/50" />}
                            {TX_LABEL[t.tx_type] || t.tx_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-white/50">{fmtDate(t.created_at)}</td>
                        <td className={`px-4 py-3 text-right font-display font-bold tabular-nums ${credit ? "text-emerald-400" : "text-white/70"}`}>
                          {credit ? "+" : "−"}RM {Math.abs(Number(t.amount)).toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
